import {
  Box, FormControl, FormControlLabel, MenuItem, Radio, RadioGroup, Select, styled, Typography,
} from '@mui/material';
import * as yup from 'yup';
import { useEffect, useState } from 'react';
import { Form, Formik } from 'formik';
import {
  FormatListBulleted, Groups3, Person, Star,
} from '@mui/icons-material';
import ArchiveIcon from '@mui/icons-material/Archive';
import KeyboardArrowRightIcon from '@mui/icons-material/KeyboardArrowRight';
import { useLocation } from 'react-router-dom';
import ClientType from '../../../models/ClientType';
import { useAppDispatch, useAppSelector } from '../../../store/Hooks';
import AlertUtils from '../../../utils/AlertUtil';
import ClientService from '../../../services/ClientService';
import ClientTypes from '../../../models/enums/ClientTypes';
import clientTypeOptions from '../../../models/ClientTypeOptions';
import Utils from '../../../utils/Utils';
import translator from '../../../theme/translator.json';
import SupportedLanguages from '../../../models/enums/SupportedLanguages';
import UserService from '../../../services/UserService';
import User from '../../../models/User';
import Spinner from '../../Spinner';
import SellerService from '../../../services/SellerService';
import Roles from '../../../models/enums/Roles';
import { setClientPopupOpen, setOrderedClientsList } from '../../../store/slices/ClientSlice';

type Props = {
    client: ClientType,
};

const ClientActions: React.FC<Props> = ({ client }) => {
  const user = useAppSelector((state) => state.user.user) as User;
  const dispatch = useAppDispatch();
  const language = useAppSelector((state) => state.user.language) as SupportedLanguages;
  const { orderedClientsList } = useAppSelector((state) => state.client);
  const location = useLocation();

  const [clientCopy, setClientCopy] = useState(client);
  const [sellers, setSellers] = useState<User[]>([]);
  const [ownerName, setOwnerName] = useState<string>('');
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    setClientCopy(client);
  }, [client]);

  useEffect(() => {
    const fetchSellersAndParent = async () => {
      if (user && user.id) {
        try {
          const sellerList = await UserService.getAllSellers(user.id);
          // Ajoute l'utilisateur parent comme une option de transfert
          const options = [...sellerList, { ...user, name: 'Moi-même (Parent)' }];
          setSellers(options);
        } catch (error) {
          console.error('Failed to fetch sellers:', error);
          setSellers([{ ...user, name: 'Moi-même (Parent)' }]); // Au moins, permettre le transfert vers soi-même
        }
      }
    };
    fetchSellersAndParent();
  }, [user]);

  useEffect(() => {
    if (!client.ownerId) {
      setOwnerName('Non assigné');
      return;
    }
    // Check if the owner is the current user
    if (client.ownerId === user.id) {
      setOwnerName(user.name || 'Moi-même');
      return;
    }
    // Check if the owner is in the sellers list
    const owner = sellers.find((seller) => seller.id === client.ownerId);
    if (owner) {
      setOwnerName(owner.name || 'Vendeur sans nom');
    } else if (sellers.length > 0) {
      setOwnerName('Propriétaire inconnu');
    }
  }, [client.ownerId, user, sellers]);

  const handleTransfer = async (newOwnerId: string) => {
    try {
      setLoading(true);
      await SellerService.transferClient(client.ownerId || user.id, newOwnerId, client.id);
      setLoading(false);
      AlertUtils.createSuccessAlert('Client transféré avec succès !', dispatch);
      dispatch(setClientPopupOpen(false));

      const newClient = {
        ownerId: newOwnerId,
      };
      setClientCopy({
        ...clientCopy,
        ...newClient,
      });
      // Mettre à jour le client dans orderedClientsList
      const updatedList = orderedClientsList.map((c: ClientType) => (c.id === client.id ? { ...c, ...clientCopy, ...newClient } : c));
      dispatch(setOrderedClientsList(updatedList));
    } catch (error) {
      console.error('Erreur lors du transfert du client:', error);
      AlertUtils.createErrorAlert('Erreur lors du transfert du client', dispatch);
      setLoading(false);
    }
  };

  const CustomRadio = styled(FormControlLabel)(({ theme }) => ({
    color: theme.palette.primary.main,
    padding: '1px 10px',
    transition: 'border 200ms ease',
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    justifyContent: 'center',
    marginLeft: 0,
    marginRight: 0,
  }));

  const renderClientTypeIcon = (option: any, value: ClientTypes) => {
    switch (option.value) {
      case ClientTypes.RESERVE:
        return (
          <>
            <FormatListBulleted color={value === option.value ? 'secondary' : 'primary'} fontSize="small" />
            <Typography sx={{ fontWeight: 600, fontSize: 14 }}>{option.label[language]}</Typography>
          </>
        );
      case ClientTypes.PROSPECT:
        return (
          <>
            <Groups3 color={value === option.value ? 'secondary' : 'primary'} fontSize="small" />
            <Typography sx={{ fontWeight: 600, fontSize: 14 }}>{option.label[language]}</Typography>
          </>
        );
      case ClientTypes.CLIENT:
        return (
          <>
            <Person color={value === option.value ? 'secondary' : 'primary'} fontSize="small" />
            <Typography sx={{ fontWeight: 600, fontSize: 14 }}>{option.label[language]}</Typography>
          </>
        );
      case ClientTypes.PROPOSITION:
        return (
          <>
            <Star color={value === option.value ? 'secondary' : 'primary'} fontSize="small" />
            <Typography sx={{ fontWeight: 600, fontSize: 14 }}>{option.label[language]}</Typography>
          </>
        );
      case ClientTypes.PEU_INTERESSANT:
        return (
          <Box sx={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'flex-start',
            gap: '10px',
          }}>
            <ArchiveIcon fontSize="small" />
            <Typography>{option?.label[language]}</Typography>
          </Box>
        );
      default:
        return <></>;
    }
  };

  return (
    <>
      <Formik
        initialValues={{
          type: clientCopy.type || ClientTypes.CLIENT,
          priority: clientCopy.priority || 99,
        }}
        enableReinitialize
        validationSchema={yup.object({
          type: yup.string(),
        })}
        onSubmit={async (values, { setSubmitting }) => {
          try {
            if (user.id) {
              const newClient = {
                type: values.type || ClientTypes.CLIENT,
                priority: values.priority || 99,
              };

              setClientCopy({
                ...clientCopy,
                ...newClient,
              });
              await ClientService.update(client.ownerId ?? user.id, client.id, newClient);

              if (location.pathname !== '/') {
                // Retirer le client de la liste actuelle (car il a changé de type)
                const filteredList = orderedClientsList.filter((c: ClientType) => c.id !== client.id);
                dispatch(setOrderedClientsList(filteredList));
              } else {
                // Sur l'onglet suivis, on retire pas le client de la liste
                const updatedList = orderedClientsList.map((c: ClientType) => (c.id === client.id ? { ...c, ...clientCopy, ...newClient } : c));
                dispatch(setOrderedClientsList(updatedList));
              }
            }
            setSubmitting(false);
            AlertUtils.createSuccessAlert(Utils.getTranslation(language, translator.successMessages.clientSaved), dispatch);
          } catch (e) {
            console.error(e);
            AlertUtils.createErrorAlert(Utils.getTranslation(language, translator.errorMessages.general.unknown), dispatch);
          }
        }}
      >
        {(formikProps) => (
          <Form>
            <Box sx={{
              display: 'flex', alignItems: 'start', justifyContent: 'space-between', gap: '40px', flexWrap: 'wrap',
            }}>
              <Box>
                <Typography sx={{ fontSize: 16, fontWeight: 600, my: 1 }}>{Utils.getTranslation(language, translator.general.clientTypeLabel)}</Typography>
                <Box sx={{
                  display: 'flex', alignItems: 'center', flexWrap: 'wrap', gap: 2,
                }}>
                  {/* TODO: abstract to component */}
                  <RadioGroup
                    name='type'
                    value={formikProps.values.type}
                    onChange={(event: any, val: string) => {
                      formikProps.setFieldValue('type', val, false);
                      formikProps.handleSubmit();
                    }}
                    sx={{
                      flexDirection: 'row',
                      flexWrap: 'wrap',
                      gap: '8px',
                    }}
                  >
                    <Box sx={{
                      display: 'flex', flexDirection: 'column', alignItems: 'flex-start', gap: 1,
                    }}>
                      <Box sx={{ display: 'flex', flexWrap: 'wrap' }}>
                        <Box sx={{
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'center',
                          my: 1,
                        }}>
                          <CustomRadio
                            sx={{
                              border: '2px solid',
                              borderColor: (formikProps.values.type === ClientTypes.PEU_INTERESSANT) ? 'secondary.main' : '#efefef',
                            }}
                            control={
                              <Radio
                                value={ClientTypes.PEU_INTERESSANT}
                                disabled={formikProps.isSubmitting}
                                sx={{
                                  display: 'none',
                                }}
                              />
                            }
                            label={
                              <Box>
                                <Box sx={{
                                  display: 'flex',
                                  alignItems: 'center',
                                  gap: 1,
                                }}>
                                  <ArchiveIcon color={formikProps.values.type === ClientTypes.PEU_INTERESSANT ? 'secondary' : 'primary'} fontSize="small" />
                                  <Typography sx={{ fontWeight: 600, fontSize: 14 }}>{Utils.getTranslation(language, translator.general.contactEnAttente)}</Typography>
                                </Box>
                              </Box>
                            }
                          />
                          <KeyboardArrowRightIcon fontSize='small' />
                        </Box>
                        {clientTypeOptions.map((option, index) => {
                          if (option.value === ClientTypes.PROPOSITION
                        || option.value === ClientTypes.RESERVE
                        || option.value === ClientTypes.PROSPECT
                        || option.value === ClientTypes.CLIENT) {
                            return (
                              <Box
                                key={index}
                                sx={{
                                  display: 'flex',
                                  alignItems: 'center',
                                  justifyContent: 'center',
                                  my: 1,
                                }}>
                                <CustomRadio
                                  sx={{
                                    border: '2px solid',
                                    borderColor: (formikProps.values.type === option.value) ? 'secondary.main' : '#efefef',
                                  }}
                                  control={
                                    <Radio
                                      value={option.value}
                                      disabled={formikProps.isSubmitting}
                                      sx={{
                                        display: 'none',
                                      }}
                                    />
                                  }
                                  label={
                                    <Box>
                                      <Box sx={{
                                        display: 'flex',
                                        alignItems: 'center',
                                        gap: 1,
                                      }}>
                                        {
                                          renderClientTypeIcon(option, formikProps.values.type)
                                        }
                                      </Box>
                                    </Box>
                                  }
                                />
                                {
                                  index !== 3
                            && <KeyboardArrowRightIcon fontSize='small' />
                                }
                              </Box>
                            );
                          }
                          return <Box key={index}></Box>;
                        })}
                      </Box>
                    </Box>

                  </RadioGroup>

                </Box>
              </Box>
              <Box>
                <Typography sx={{ fontSize: 16, fontWeight: 600, my: 1 }}>{Utils.getTranslation(language, translator.formLabels.priority)}</Typography>
                <FormControl sx={{ minWidth: '180px' }}>
                  <Select
                    value={formikProps.values.priority}
                    name='priority'
                    onChange={(event: any) => {
                      formikProps.setFieldValue('priority', event.target.value, false);
                      formikProps.handleSubmit();
                    }}
                  >
                    <MenuItem value={99}>{Utils.getTranslation(language, translator.general.aucune)}</MenuItem>
                    {
                      [1, 2, 3].map((option: any, index: number) => (
                        <MenuItem key={index} value={option}>{option}</MenuItem>
                      ))
                    }
                  </Select>
                </FormControl>
              </Box>
              {sellers.length > 1 && (user.role === Roles.USER) && (
                <Box sx={{
                  display: 'flex', alignItems: 'start', gap: 3, my: 1,
                }}>
                  <Box>
                    <Typography sx={{ fontSize: 16, fontWeight: 600, mb: 0.5 }}>Propriétaire actuel</Typography>
                    <Typography>{ownerName}</Typography>
                  </Box>
                  <Box>
                    <Typography sx={{ fontSize: 16, fontWeight: 600, mb: 0.5 }}>Transférer le client</Typography>
                    <FormControl sx={{ minWidth: '180px' }}>
                      <Select
                        size="small"
                        displayEmpty
                        value=""
                        onChange={(e) => handleTransfer(e.target.value as string)}
                      >
                        <MenuItem value="" disabled>
                          <em>Sélectionner un nouveau propriétaire</em>
                        </MenuItem>
                        {sellers.map((seller) => (
                          <MenuItem key={seller.id} value={seller.id} disabled={client.ownerId === seller.id}>
                            {seller.name}
                          </MenuItem>
                        ))}
                      </Select>
                    </FormControl>
                  </Box>
                </Box>
              )}
            </Box>
          </Form>
        )}
      </Formik>
      <Spinner show={loading} />
    </>
  );
};

export default ClientActions;
